import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { ErrorBanner, Field, NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError, api } from '@/lib/api';
import { formatTrn, useClients, useMe, type Client } from '@/lib/clients';
import { usePracticeOverview, type PracticeOverviewRow } from '@/lib/dashboard';
import { todayIso } from '@/lib/dates';
import { formatDate } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { TONE_CLASS, vatStatusChip } from '@/lib/vat';
import { GST_STATES, gstinProblem, stateLabel } from '@/lib/gst';
import { useOrgId } from '@/lib/org';
import { useOpenClient } from '@/lib/use-open-client';

export const Route = createFileRoute('/practice/clients')({ component: ClientsPage });

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;
/** The emirate as the API takes it, and its message key. */
const EMIRATES = [
  ['Abu Dhabi', 'abuDhabi'],
  ['Dubai', 'dubai'],
  ['Sharjah', 'sharjah'],
  ['Ajman', 'ajman'],
  ['Umm Al Quwain', 'ummAlQuwain'],
  ['Ras Al Khaimah', 'rasAlKhaimah'],
  ['Fujairah', 'fujairah'],
] as const;
const TEMPLATES = ['trading', 'services', 'free_zone', 'india_trading', 'india_services'] as const;

/** Screen P1 (document 06): My practice › Clients, list pattern. */
function ClientsPage() {
  const clients = useClients();
  const overview = usePracticeOverview();
  const status = new Map((overview.data ?? []).map((o) => [o.orgId, o]));
  const today = todayIso();
  const me = useMe();
  const orgId = useOrgId();
  const openClient = useOpenClient();
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const t = useT();

  const needle = q.trim().toLowerCase().replace(/\s+/g, '');
  const rows = (clients.data ?? []).filter(
    (c) =>
      !needle ||
      c.name.toLowerCase().replace(/\s+/g, '').includes(needle) ||
      (c.trn ?? '').includes(needle) ||
      (c.gstin ?? '').toLowerCase().includes(needle),
  );

  return (
    <>
      <PageHeader
        title={t('accountingPages.practiceClients.title')}
        description={t('accountingPages.practiceClients.description')}
        actions={
          me.data?.isStaff ? (
            <Button onClick={() => setAdding(true)}>
              {t('accountingPages.practiceClients.addClient')}
            </Button>
          ) : null
        }
      />
      <div className="mb-3 flex items-center gap-3">
        <div className="relative w-[320px]">
          <Search className="pointer-events-none absolute start-2 top-2.5 size-4 text-muted-foreground" />
          <Input
            className="bg-card ps-8"
            placeholder={t('accountingPages.practiceClients.search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label={t('accountingPages.practiceClients.searchLabel')}
          />
        </div>
        {clients.data ? (
          <span className="text-sm text-muted-foreground">
            {t('accountingPages.practiceClients.countOf', {
              shown: rows.length,
              count: clients.data.length,
            })}
          </span>
        ) : null}
      </div>
      {clients.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : clients.isError ? (
        <EmptyState
          title={t('accountingPages.practiceClients.couldNotLoad')}
          hint={clients.error.message}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title={
            clients.data.length === 0
              ? t('accountingPages.practiceClients.emptyTitle')
              : t('accountingPages.practiceClients.noMatch')
          }
          hint={
            clients.data.length === 0 ? t('accountingPages.practiceClients.emptyHint') : undefined
          }
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('accountingPages.practiceClients.col.client')}</TableHead>
                <TableHead>{t('accountingPages.practiceClients.col.taxNumber')}</TableHead>
                <TableHead>{t('accountingPages.practiceClients.col.taxPeriod')}</TableHead>
                <TableHead className="text-end">
                  {t('accountingPages.practiceClients.col.bankItems')}
                </TableHead>
                <TableHead className="text-end">
                  {t('accountingPages.practiceClients.col.unreconciled')}
                </TableHead>
                <TableHead>{t('accountingPages.practiceClients.col.lastLocked')}</TableHead>
                <TableHead>{t('accountingPages.practiceClients.col.accountants')}</TableHead>
                <TableHead className="w-[140px] text-end">
                  {t('accountingPages.practiceClients.col.action')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.id} className={c.id === orgId ? 'bg-accent/60' : ''}>
                  <TableCell className="font-medium">
                    <button
                      type="button"
                      className="text-start hover:text-primary hover:underline"
                      onClick={() => openClient(c.id, { toDashboard: true })}
                    >
                      {c.name}
                    </button>
                    {c.id === orgId ? (
                      <Badge variant="secondary" className="ms-2">
                        {t('accountingPages.practiceClients.open')}
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell className="tnum">
                    {c.taxPack === 'in' ? (c.gstin ?? '') : formatTrn(c.trn)}
                  </TableCell>
                  <ComplianceCells
                    row={status.get(c.id)}
                    loading={overview.isPending}
                    today={today}
                    taxPeriod={c.taxPeriod}
                    india={c.taxPack === 'in'}
                  />
                  <TableCell className="max-w-[180px] truncate">
                    {c.accountants.join(', ')}
                  </TableCell>
                  <TableCell className="text-end">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openClient(c.id, { toDashboard: true })}
                    >
                      {t('accountingPages.practiceClients.openBooks')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <AddClientDrawer open={adding} onOpenChange={setAdding} />
    </>
  );
}

/** Tax return status, bank items, unreconciled accounts and lock date of one client (P1). */
function ComplianceCells({
  row,
  loading,
  today,
  taxPeriod,
  india,
}: {
  row: PracticeOverviewRow | undefined;
  loading: boolean;
  today: string;
  taxPeriod: string;
  india: boolean;
}) {
  const t = useT();
  if (!row)
    return (
      <>
        <TableCell colSpan={4} className="text-xs text-muted-foreground">
          {loading ? t('common.loading') : ''}
        </TableCell>
      </>
    );
  if (row.error)
    return (
      <TableCell colSpan={4} className="text-xs text-destructive" title={row.error}>
        {t('accountingPages.practiceClients.statusError')}
      </TableCell>
    );
  const chip = row.vat ? vatStatusChip(row.vat, today) : null;
  /** The chip's words in the reader's language (vatStatusChip gives the English). */
  const chipLabel = (v: NonNullable<PracticeOverviewRow['vat']>): string => {
    switch (v.status) {
      case 'due': {
        const days = Math.round((Date.parse(v.dueDate) - Date.parse(today)) / 86_400_000);
        return days === 0
          ? t('accountingPages.practiceClients.chip.dueToday')
          : t('accountingPages.practiceClients.chip.dueIn', { count: days });
      }
      case 'open':
      case 'overdue':
      case 'filed':
      case 'paid':
        return t(`accountingPages.practiceClients.chip.${v.status}`);
      default:
        return chip?.label ?? '';
    }
  };
  return (
    <>
      <TableCell>
        {!row.vatRegistered ? (
          <span className="text-xs text-muted-foreground">
            {india
              ? t('accountingPages.practiceClients.notRegisteredGst')
              : t('accountingPages.practiceClients.notRegisteredVat')}
          </span>
        ) : row.vat && chip ? (
          <div className="flex flex-col items-start gap-0.5">
            <span
              className={cn('rounded-full border px-2 text-xs', TONE_CLASS[chip.tone])}
              data-testid="vat-chip"
            >
              {chipLabel(row.vat)}
            </span>
            <span className="text-xs text-muted-foreground">
              {t('accountingPages.practiceClients.due', {
                label: row.vat.label,
                date: formatDate(row.vat.dueDate),
              })}
              {row.vat.overdueReturns > 1
                ? ` · ${t('accountingPages.practiceClients.overdue', { count: row.vat.overdueReturns })}`
                : ''}
            </span>
          </div>
        ) : (
          <span className="text-xs capitalize text-muted-foreground">
            {taxPeriod === 'monthly' || taxPeriod === 'quarterly'
              ? t(`accountingPages.practiceClients.filed.${taxPeriod}`)
              : t('accountingPages.practiceClients.filedOther', { period: taxPeriod })}
          </span>
        )}
      </TableCell>
      <TableCell className="tnum text-end">{row.bankItemsToReview || ''}</TableCell>
      <TableCell className="tnum text-end">{row.unreconciledAccounts || ''}</TableCell>
      <TableCell>
        {row.lastLockedPeriod ? formatDate(row.lastLockedPeriod) : t('common.none')}
      </TableCell>
    </>
  );
}

interface ClientForm {
  name: string;
  legalName: string;
  /** IN (India GST, the default) or AE (UAE VAT). */
  country: string;
  gstin: string;
  stateCode: string;
  emirate: string;
  trn: string;
  fiscalYearStart: string;
  taxPeriod: string;
  chartTemplate: string;
}

const EMPTY: ClientForm = {
  name: '',
  legalName: '',
  country: 'IN',
  gstin: '',
  stateCode: '27',
  emirate: 'Dubai',
  trn: '',
  fiscalYearStart: '4',
  taxPeriod: 'monthly',
  chartTemplate: 'india_trading',
};

/** What the country decides: home currency, tax, financial year, return period and chart. */
const COUNTRY_DEFAULTS: Record<
  string,
  {
    /** Key of the home currency's label. */
    currency: 'IN' | 'AE';
    tax: string;
    taxNumber: string;
    fy: string;
    period: string;
    chart: string;
  }
> = {
  IN: {
    currency: 'IN',
    tax: 'GST',
    taxNumber: 'GSTIN',
    fy: '4',
    period: 'monthly',
    chart: 'india_trading',
  },
  AE: {
    currency: 'AE',
    tax: 'VAT',
    taxNumber: 'TRN',
    fy: '1',
    period: 'quarterly',
    chart: 'trading',
  },
};

function AddClientDrawer({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const openClient = useOpenClient();
  const t = useT();
  const [form, setForm] = useState<ClientForm>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof ClientForm, string>>>({});
  const set = (k: keyof ClientForm) => (v: string) => setForm((f) => ({ ...f, [k]: v }));
  const india = form.country === 'IN';
  const defaults = COUNTRY_DEFAULTS[form.country]!;
  /** The country sets the currency, tax, financial year, return period and chart. */
  const setCountry = (country: string) => {
    const d = COUNTRY_DEFAULTS[country]!;
    setForm((f) => ({
      ...f,
      country,
      fiscalYearStart: d.fy,
      taxPeriod: d.period,
      chartTemplate: d.chart,
    }));
    setErrors({});
  };

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<Client>('/practice/clients', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: async (client) => {
      await qc.invalidateQueries({ queryKey: ['practice', 'clients'] });
      toast(t('accountingPages.practiceClients.added', { name: client.name }));
      setForm(EMPTY);
      onOpenChange(false);
      openClient(client.id, { toDashboard: true });
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const trn = form.trn.replace(/\s+/g, '');
    const next: typeof errors = {};
    if (form.name.trim().length < 2) next.name = t('accountingPages.practiceClients.error.name');
    const gstin = form.gstin.replace(/\s+/g, '').toUpperCase();
    if (!india && trn && !/^[0-9]{15}$/.test(trn))
      next.trn = t('accountingPages.practiceClients.error.trn');
    if (india && gstin) {
      const problem = gstinProblem(gstin);
      if (problem) next.gstin = problem;
      else if (gstin.slice(0, 2) !== form.stateCode)
        next.stateCode = t('accountingPages.practiceClients.error.gstinState', {
          state: stateLabel(gstin.slice(0, 2)),
        });
    }
    setErrors(next);
    if (Object.keys(next).length) return;
    create.mutate({
      name: form.name.trim(),
      ...(form.legalName.trim() ? { legalName: form.legalName.trim() } : {}),
      country: form.country,
      ...(india
        ? { stateCode: form.stateCode, ...(gstin ? { gstin } : {}) }
        : { ...(form.emirate ? { emirate: form.emirate } : {}), ...(trn ? { trn } : {}) }),
      fiscalYearStart: Number(form.fiscalYearStart),
      taxPeriod: form.taxPeriod,
      chartTemplate: form.chartTemplate,
    });
  }

  const serverError =
    create.error instanceof ApiError
      ? create.error.message
      : create.error
        ? String(create.error)
        : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md">
        <form onSubmit={submit} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle>{t('accountingPages.practiceClients.drawerTitle')}</SheetTitle>
            <SheetDescription>
              {t('accountingPages.practiceClients.drawerDescription')}
            </SheetDescription>
          </SheetHeader>
          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            <ErrorBanner>{serverError}</ErrorBanner>
            <Field
              label={t('accountingPages.practiceClients.country')}
              hint={t('accountingPages.practiceClients.countryHint')}
            >
              {(id) => (
                <NativeSelect
                  id={id}
                  value={form.country}
                  onChange={(e) => setCountry(e.target.value)}
                  autoFocus
                >
                  <option value="IN">{t('accountingPages.practiceClients.india')}</option>
                  <option value="AE">{t('accountingPages.practiceClients.uae')}</option>
                </NativeSelect>
              )}
            </Field>
            <Field label={t('accountingPages.practiceClients.clientName')} error={errors.name}>
              {(id) => (
                <Input
                  id={id}
                  value={form.name}
                  maxLength={200}
                  onChange={(e) => set('name')(e.target.value)}
                  aria-invalid={Boolean(errors.name)}
                />
              )}
            </Field>
            <Field
              label={t('accountingPages.practiceClients.legalName')}
              hint={
                india
                  ? t('accountingPages.practiceClients.legalNameHintIndia')
                  : t('accountingPages.practiceClients.legalNameHintUae')
              }
            >
              {(id) => (
                <Input
                  id={id}
                  value={form.legalName}
                  maxLength={200}
                  onChange={(e) => set('legalName')(e.target.value)}
                />
              )}
            </Field>
            <div className="grid grid-cols-1 gap-3">
              {india ? (
                <Field label={t('accountingPages.practiceClients.state')} error={errors.stateCode}>
                  {(id) => (
                    <NativeSelect
                      id={id}
                      value={form.stateCode}
                      onChange={(e) => set('stateCode')(e.target.value)}
                    >
                      {GST_STATES.map(([code, name]) => (
                        <option key={code} value={code}>
                          {code} · {name}
                        </option>
                      ))}
                    </NativeSelect>
                  )}
                </Field>
              ) : (
                <Field label={t('accountingPages.practiceClients.emirate')}>
                  {(id) => (
                    <NativeSelect
                      id={id}
                      value={form.emirate}
                      onChange={(e) => set('emirate')(e.target.value)}
                    >
                      {EMIRATES.map(([em, key]) => (
                        <option key={em} value={em}>
                          {t(`accountingPages.practiceClients.emirates.${key}`)}
                        </option>
                      ))}
                    </NativeSelect>
                  )}
                </Field>
              )}
            </div>
            {india ? (
              <Field
                label={t('accountingPages.practiceClients.gstin')}
                hint={t('accountingPages.practiceClients.gstinHint')}
                error={errors.gstin}
              >
                {(id) => (
                  <Input
                    id={id}
                    value={form.gstin}
                    placeholder="27AAGCG4321K1Z5"
                    onChange={(e) => {
                      const v = e.target.value.toUpperCase();
                      set('gstin')(v);
                      const code = v.replace(/\s+/g, '').slice(0, 2);
                      if (/^\d{2}$/.test(code) && GST_STATES.some(([c]) => c === code))
                        set('stateCode')(code);
                    }}
                    aria-invalid={Boolean(errors.gstin)}
                  />
                )}
              </Field>
            ) : (
              <Field
                label={t('accountingPages.practiceClients.trn')}
                hint={t('accountingPages.practiceClients.trnHint')}
                error={errors.trn}
              >
                {(id) => (
                  <Input
                    id={id}
                    value={form.trn}
                    inputMode="numeric"
                    placeholder="100 1234567 00 003"
                    onChange={(e) => set('trn')(e.target.value)}
                    aria-invalid={Boolean(errors.trn)}
                  />
                )}
              </Field>
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label={t('accountingPages.practiceClients.homeCurrency')}>
                {(id) => (
                  <Input
                    id={id}
                    value={t(`accountingPages.practiceClients.currency.${defaults.currency}`)}
                    disabled
                  />
                )}
              </Field>
              <Field label={t('accountingPages.practiceClients.firstMonth')}>
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={form.fiscalYearStart}
                    onChange={(e) => set('fiscalYearStart')(e.target.value)}
                  >
                    {MONTHS.map((m) => (
                      <option key={m} value={m}>
                        {t(`accountingPages.recurringSchedule.month.m${m}`)}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
            </div>
            <Field
              label={
                india
                  ? t('accountingPages.practiceClients.returnPeriodGst')
                  : t('accountingPages.practiceClients.returnPeriodVat')
              }
            >
              {(id) => (
                <NativeSelect
                  id={id}
                  value={form.taxPeriod}
                  onChange={(e) => set('taxPeriod')(e.target.value)}
                >
                  <option value="quarterly">
                    {t('accountingPages.practiceClients.quarterly')}
                  </option>
                  <option value="monthly">{t('accountingPages.practiceClients.monthly')}</option>
                </NativeSelect>
              )}
            </Field>
            <Field label={t('accountingPages.practiceClients.chartTemplate')}>
              {(id) => (
                <NativeSelect
                  id={id}
                  value={form.chartTemplate}
                  onChange={(e) => set('chartTemplate')(e.target.value)}
                >
                  {TEMPLATES.filter((v) => v.startsWith('india_') === india).map((v) => (
                    <option key={v} value={v}>
                      {t(`accountingPages.practiceClients.template.${v}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
          </div>
          <SheetFooter className="flex-row justify-end border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? t('common.saving') : t('common.save')}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
